import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { apiPathV4 } from '../config';
import { getAuthToken } from '../utils/authUtils';
import {
  adjustActualUnreadCount,
  hideNotificationLocally,
  readHiddenNotificationIds,
} from '../utils/notificationUtils';
import { scheduleDeferred } from '../utils/scheduleDeferred';
import { useAppAuth } from './AppContext';

type FeatureAnnouncementDetails = {
  notification_tier?: number | null;
  notification_tiers?: number[] | null;
  bell_copy?: string | null;
  banner_copy?: string | null;
  icon?: string | null;
  cta_primary?: { label: string; url: string } | null;
  cta_secondary?: { label: string; url: string } | null;
  video_url?: string | null;
  external_link?: string | null;
  display_order?: number | null;
};

export type FeatureAnnouncement = {
  id: number;
  title: string;
  content: string;
  details?: FeatureAnnouncementDetails | null;
  priority?: number | null;
  starts_at?: string | null;
  expires_at?: string | null;
  // Present on user-scoped rows. Global/account-scoped rows always come back as
  // null from the API today (see notification_functions.list_notifications).
  user_read_at?: string | null;
  user_id?: string | null;
  account_id?: number | null;
  role?: string | null;
};

type FeatureAnnouncementsResponse = {
  announcements?: FeatureAnnouncement[];
};

/** keepOnError: a failed refresh keeps the announcements already loaded. */
type RefreshOptions = { keepOnError?: boolean };

type AnnouncementsContextType = {
  // All active announcements for this user (tier-1 + tier-2, global/account/user
  // scoped). Not filtered by read state — consumed by the bell panel's
  // Announcements tab so global/account rows (which API cannot mark read per
  // user today) still render.
  allAnnouncements: FeatureAnnouncement[];
  // Tier-2 only, with user_read_at and session-dismissed rows filtered out.
  // Consumed by the banner.
  tierTwoAnnouncements: FeatureAnnouncement[];
  dismissedBannerIds: Set<number>;
  isLoading: boolean;
  refreshAnnouncements: (options?: RefreshOptions) => Promise<void>;
  dismissBannerForSession: (announcementId: number) => void;
  dismissBanner: (announcementId: number) => Promise<void>;
};

const SESSION_BANNER_KEY = 'feature_announcement_banner_dismissed_';
const LOCAL_STATE_SYNC_EVENT = 'notifications:refresh-unread-count';
const LOCAL_STATE_SOURCE = 'announcements-context';

type LocalStateSyncEventDetail = {
  source?: string;
  notificationId?: number;
  unreadCount?: number;
  hiddenIds?: number[];
};

const AnnouncementsContext = createContext<AnnouncementsContextType | null>(null);

const getAnnouncementTiers = (announcement: FeatureAnnouncement): number[] => {
  const tiers = new Set<number>();
  const details = announcement.details ?? {};

  if (Array.isArray(details.notification_tiers)) {
    details.notification_tiers.forEach(tier => {
      if (tier === 1 || tier === 2) {
        tiers.add(tier);
      }
    });
  }

  if (details.notification_tier === 1 || details.notification_tier === 2) {
    tiers.add(details.notification_tier);
  }

  if (announcement.priority === 1 || announcement.priority === 2) {
    tiers.add(announcement.priority);
  }

  return Array.from(tiers);
};

export function useAnnouncements(): AnnouncementsContextType {
  const context = useContext(AnnouncementsContext);
  if (!context) {
    throw new Error('useAnnouncements must be used within an AnnouncementsProvider');
  }
  return context;
}

export function AnnouncementsProvider({
  children,
}: {
  children: React.ReactNode;
}): React.JSX.Element {
  const {
    isAuthenticated,
    isLoading: isAuthLoading,
    user,
    getIdTokenClaims,
    getAccessTokenSilently,
  } = useAppAuth();
  const [announcements, setAnnouncements] = useState<FeatureAnnouncement[]>([]);
  const [dismissedBannerIds, setDismissedBannerIds] = useState<Set<number>>(new Set());
  const [sessionHiddenIds, setSessionHiddenIds] = useState<Set<number>>(() =>
    readHiddenNotificationIds()
  );
  const [isLoading, setIsLoading] = useState(true);

  const getToken = useCallback(async (): Promise<string | null> => {
    let token = await getAuthToken(getIdTokenClaims);
    if (!token) {
      token = await getAccessTokenSilently();
    }
    return token;
  }, [getAccessTokenSilently, getIdTokenClaims]);

  const dismissBannerForSession = useCallback((announcementId: number): void => {
    const persistedHiddenIds = readHiddenNotificationIds();
    if (persistedHiddenIds.has(announcementId) || dismissedBannerIds.has(announcementId)) {
      return;
    }

    try {
      localStorage.setItem(`${SESSION_BANNER_KEY}${announcementId}`, 'true');
    } catch {
      // no-op
    }
    const nextHiddenIds = hideNotificationLocally(announcementId);
    const nextUnreadCount = adjustActualUnreadCount({ actualDelta: -1 });
    setSessionHiddenIds(nextHiddenIds);
    setDismissedBannerIds(prev => new Set(prev).add(announcementId));
    window.dispatchEvent(
      new CustomEvent<LocalStateSyncEventDetail>(LOCAL_STATE_SYNC_EVENT, {
        detail: {
          source: LOCAL_STATE_SOURCE,
          notificationId: announcementId,
          unreadCount: nextUnreadCount,
          hiddenIds: Array.from(nextHiddenIds),
        },
      })
    );
  }, [dismissedBannerIds]);

  const dismissBanner = useCallback(
    async (announcementId: number): Promise<void> => {
      dismissBannerForSession(announcementId);
    },
    [dismissBannerForSession]
  );

  const refreshAnnouncements = useCallback(async (options?: RefreshOptions) => {
    if (!isAuthenticated) {
      setAnnouncements([]);
      setSessionHiddenIds(readHiddenNotificationIds());
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const token = await getToken();

      if (!token) {
        setAnnouncements([]);
        return;
      }

      // Single fetch for both banner and panel. The response includes global,
      // account, and user-scoped rows; we derive the two consumer lists below.
      const response = await fetch(`${apiPathV4()}/announcements/me`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to load announcements (${response.status})`);
      }

      const data = (await response.json()) as FeatureAnnouncementsResponse;
      const items = Array.isArray(data.announcements) ? data.announcements : [];
      setAnnouncements(items);
      setSessionHiddenIds(readHiddenNotificationIds());

      const nextDismissed = new Set<number>();
      for (const announcement of items) {
        try {
          if (localStorage.getItem(`${SESSION_BANNER_KEY}${announcement.id}`) === 'true') {
            nextDismissed.add(announcement.id);
          }
        } catch {
          // ignore storage access issues
        }
      }
      setDismissedBannerIds(nextDismissed);
    } catch (error) {
      console.error('[ANNOUNCEMENTS] Failed to load announcements:', error);
      if (!options?.keepOnError) {
        setAnnouncements([]);
        setDismissedBannerIds(new Set());
        setSessionHiddenIds(readHiddenNotificationIds());
      }
    } finally {
      setIsLoading(false);
    }
  }, [getToken, isAuthenticated]);

  useEffect(() => {
    if (isAuthLoading) {
      return;
    }

    if (!isAuthenticated) {
      setAnnouncements([]);
      setDismissedBannerIds(new Set());
      setSessionHiddenIds(readHiddenNotificationIds());
      setIsLoading(false);
      return;
    }

    // Defer off the cold-load critical path; banner/panel fill after first paint.
    return scheduleDeferred(() => {
      void refreshAnnouncements();
    });
  }, [
    isAuthLoading,
    isAuthenticated,
    refreshAnnouncements,
    user?.sub,
    user?.ept_app_metadata?.account_id,
    user?.ept_roles,
  ]);

  useEffect(() => {
    const handleNotificationVisibilityChange = (event: Event) => {
      const detail = (event as CustomEvent<LocalStateSyncEventDetail>).detail;
      if (detail?.source === LOCAL_STATE_SOURCE) {
        return;
      }

      const nextHiddenIds = Array.isArray(detail?.hiddenIds)
        ? new Set(detail.hiddenIds)
        : readHiddenNotificationIds();
      setSessionHiddenIds(nextHiddenIds);
    };

    window.addEventListener(
      LOCAL_STATE_SYNC_EVENT,
      handleNotificationVisibilityChange
    );
    return () => {
      window.removeEventListener(
        LOCAL_STATE_SYNC_EVENT,
        handleNotificationVisibilityChange
      );
    };
  }, []);

  const tierTwoAnnouncements = useMemo(
    () =>
      announcements.filter(
        announcement =>
          getAnnouncementTiers(announcement).includes(2) &&
          !dismissedBannerIds.has(announcement.id) &&
          !announcement.user_read_at &&
          !sessionHiddenIds.has(announcement.id)
      ),
    [announcements, dismissedBannerIds, sessionHiddenIds]
  );

  const value = useMemo(
    () => ({
      allAnnouncements: announcements,
      tierTwoAnnouncements,
      dismissedBannerIds,
      isLoading,
      refreshAnnouncements,
      dismissBannerForSession,
      dismissBanner,
    }),
    [
      announcements,
      tierTwoAnnouncements,
      dismissedBannerIds,
      isLoading,
      refreshAnnouncements,
      dismissBannerForSession,
      dismissBanner,
    ]
  );

  return <AnnouncementsContext.Provider value={value}>{children}</AnnouncementsContext.Provider>;
}
